/**
 * App shell — Phase 2.
 *
 * Boot order is the whole point of this component:
 *   1. `hydrateAppStore()` restores the persisted session and, on web,
 *      reactivates the per-store database. Nothing may touch `database`
 *      before it resolves, so the tree does not render until it does.
 *   2. With a session and a store, mount the router.
 *   3. Otherwise show login.
 *
 * A reload lands here with the access token gone (it lives in memory only) but
 * the refresh token still in sessionStorage, so `httpClient` re-mints silently
 * on the first request. See services/tokenStore.ts.
 *
 * Routes mirror Desktop's paths exactly, so ported screens' internal
 * `navigate()` calls work unchanged. Only the billing routes exist so far —
 * `AppShell.IMPLEMENTED_ROUTES` hides nav links to the rest.
 */
import { useEffect, useState } from 'react';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { hydrateAppStore, useAppStore } from '@/store/useAppStore';
import { tokenStore } from '@/services/tokenStore';
import { LoginScreen } from '@/screens/LoginScreen';
import { RegistrationScreen } from '@/screens/onboarding/RegistrationScreen';
import { WaitingApprovalScreen } from '@/screens/onboarding/WaitingApprovalScreen';
import { RejectedScreen } from '@/screens/onboarding/RejectedScreen';
import { StoreGate } from '@/screens/onboarding/StoreGate';
import { StatusScreen } from '@/screens/StatusScreen';
import { DashboardScreen } from '@/screens/DashboardScreen';
import { AppShell } from '@/screens/AppShell';
import { BillingScreen } from '@/screens/billing/BillingScreen';
import { InvoiceListScreen } from '@/screens/billing/InvoiceListScreen';
import { InvoiceDetailScreen } from '@/screens/billing/InvoiceDetailScreen';
import { ReturnInvoiceScreen } from '@/screens/billing/ReturnInvoiceScreen';
import { InventoryListScreen } from '@/screens/inventory/InventoryListScreen';
import { BulkImportScreen } from '@/screens/inventory/BulkImportScreen';
import { AiImportScreen } from '@/screens/inventory/AiImportScreen';
import { SettingsScreen } from '@/screens/settings/SettingsScreen';
import { ReportsScreen } from '@/screens/reports/ReportsScreen';
import { CustomerListScreen } from '@/screens/customers/CustomerListScreen';
import { CustomerStatementScreen } from '@/screens/customers/CustomerStatementScreen';
import { ExpenseListScreen } from '@/screens/expenses/ExpenseListScreen';
import { PromotionsScreen } from '@/screens/promotions/PromotionsScreen';
import { SupplierListScreen } from '@/screens/purchases/SupplierListScreen';
import { PurchaseEntryScreen } from '@/screens/purchases/PurchaseEntryScreen';
import { PurchaseOrderListScreen } from '@/screens/purchases/PurchaseOrderListScreen';
import { ReceivePurchaseOrderScreen } from '@/screens/purchases/ReceivePurchaseOrderScreen';
import { ActivityLogScreen } from '@/screens/ActivityLogScreen';
import {
  countUnsyncedRecords,
  installUnloadGuard,
  isDatabaseReady,
  requestPersistentStorage,
  useDbHealthStore,
} from '@/db';
import { BrandLoader } from '@/components/BrandLoader';
import {
  ACTIVITY_LOG_VIEW,
  BILLING_CREATE,
  BILLING_CREDIT_NOTE,
  BILLING_VIEW,
  CUSTOMER_VIEW,
  INVENTORY_IMPORT,
  INVENTORY_VIEW,
  PURCHASING_VIEW,
  REPORTS_VIEW,
  SETTINGS_MANAGE,
  usePermissions,
  type Permission,
} from '@/rbac';

/**
 * Route-level RBAC guard, matching BigxBookDesktop/src/app/App.tsx.
 *
 * Hiding a nav link is presentation; this is the actual gate. Without it a
 * cashier could reach an inventory or credit-note screen by typing the URL —
 * more likely on web than on Desktop, where there is no address bar.
 */
function RequirePermission({
  permission,
  children,
}: {
  permission: Permission;
  children: JSX.Element;
}) {
  const { can } = usePermissions();
  if (!can(permission)) {
    return (
      <div className="flex h-full items-center justify-center px-6 text-center">
        <div>
          <h1 className="text-heading text-fg-primary">Access denied</h1>
          <p className="mt-1 text-body text-fg-secondary">Your role does not allow this action.</p>
        </div>
      </div>
    );
  }
  return children;
}

/**
 * E2E test bridge — same double gate as Desktop: it must be compiled in
 * (`VITE_ENABLE_E2E=true`, off for production builds) *and* explicitly asked
 * for via `?e2e=1`. Two gates rather than one because this bridge can seed and
 * export the local database, which must never be reachable on a real till.
 */
const e2eBridgeEnabled = import.meta.env.VITE_ENABLE_E2E === 'true';

if (
  e2eBridgeEnabled &&
  typeof window !== 'undefined' &&
  new URLSearchParams(window.location.search).get('e2e') === '1'
) {
  void import('@/e2e/bridge');
}

const router = createBrowserRouter([
  {
    element: <AppShell />,
    children: [
      { path: '/', element: <DashboardScreen /> },
      // The Phase 0-1 boot diagnostic. Kept on its own route rather than
      // deleted: it is the only place that surfaces device slot, registration
      // state, unsynced count and IndexedDB persistence, which are exactly what
      // you need when a till syncs badly. Not in the nav — support-facing.
      { path: '/diagnostics', element: <StatusScreen /> },
      // Guarded like every other route. Hiding the nav link is presentation;
      // this is the gate. On web especially, a role without BILLING_CREATE
      // could simply type /billing and take a sale — and because the backend
      // strips invoices it may not write, that sale then vanished at push
      // instead of failing at the till. See also the check in useCreateInvoice,
      // which is the boundary that actually matters.
      {
        path: '/billing',
        element: (
          <RequirePermission permission={BILLING_CREATE}>
            <BillingScreen />
          </RequirePermission>
        ),
      },
      {
        path: '/invoices',
        element: (
          <RequirePermission permission={BILLING_VIEW}>
            <InvoiceListScreen />
          </RequirePermission>
        ),
      },
      {
        path: '/invoices/:invoiceId',
        element: (
          <RequirePermission permission={BILLING_VIEW}>
            <InvoiceDetailScreen />
          </RequirePermission>
        ),
      },
      {
        path: '/invoices/:invoiceId/return',
        element: (
          <RequirePermission permission={BILLING_CREDIT_NOTE}>
            <ReturnInvoiceScreen />
          </RequirePermission>
        ),
      },

      // ── Phase 3: inventory & product management ──────────────────────────
      {
        path: '/inventory',
        element: (
          <RequirePermission permission={INVENTORY_VIEW}>
            <InventoryListScreen />
          </RequirePermission>
        ),
      },
      {
        path: '/inventory/import',
        element: (
          <RequirePermission permission={INVENTORY_IMPORT}>
            <BulkImportScreen />
          </RequirePermission>
        ),
      },
      // ── Phase 5: reports ─────────────────────────────────────────────────
      {
        path: '/reports',
        element: (
          <RequirePermission permission={REPORTS_VIEW}>
            <ReportsScreen />
          </RequirePermission>
        ),
      },
      {
        path: '/reports/:reportId',
        element: (
          <RequirePermission permission={REPORTS_VIEW}>
            <ReportsScreen />
          </RequirePermission>
        ),
      },

      // ── Phase 4: user management (Staff lives inside Settings) ───────────
      {
        path: '/settings',
        element: (
          <RequirePermission permission={SETTINGS_MANAGE}>
            <SettingsScreen />
          </RequirePermission>
        ),
      },
      {
        path: '/inventory/ai-import',
        element: (
          <RequirePermission permission={INVENTORY_IMPORT}>
            <AiImportScreen />
          </RequirePermission>
        ),
      },

      // ── Phase 6: remaining parity ────────────────────────────────────────
      {
        path: '/customers',
        element: (
          <RequirePermission permission={CUSTOMER_VIEW}>
            <CustomerListScreen />
          </RequirePermission>
        ),
      },
      {
        path: '/customers/:customerId',
        element: (
          <RequirePermission permission={CUSTOMER_VIEW}>
            <CustomerStatementScreen />
          </RequirePermission>
        ),
      },
      {
        path: '/expenses',
        element: (
          <RequirePermission permission={REPORTS_VIEW}>
            <ExpenseListScreen />
          </RequirePermission>
        ),
      },
      {
        path: '/promotions',
        element: (
          <RequirePermission permission={SETTINGS_MANAGE}>
            <PromotionsScreen />
          </RequirePermission>
        ),
      },
      {
        path: '/suppliers',
        element: (
          <RequirePermission permission={PURCHASING_VIEW}>
            <SupplierListScreen />
          </RequirePermission>
        ),
      },
      {
        path: '/purchases/new',
        element: (
          <RequirePermission permission={PURCHASING_VIEW}>
            <PurchaseEntryScreen />
          </RequirePermission>
        ),
      },
      {
        path: '/purchase-orders',
        element: (
          <RequirePermission permission={PURCHASING_VIEW}>
            <PurchaseOrderListScreen />
          </RequirePermission>
        ),
      },
      {
        path: '/purchase-orders/:poId/receive',
        element: (
          <RequirePermission permission={PURCHASING_VIEW}>
            <ReceivePurchaseOrderScreen />
          </RequirePermission>
        ),
      },
      {
        path: '/activity',
        element: (
          <RequirePermission permission={ACTIVITY_LOG_VIEW}>
            <ActivityLogScreen />
          </RequirePermission>
        ),
      },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
]);

/**
 * Ends the session without touching local data.
 *
 * BIGXBOOK_WEB_PLAN Decision 3: logout never wipes the local database. A
 * browser can hold invoices that exist nowhere else, and "sign out" is not a
 * request to destroy them — the merchant is expected to sign back in on the
 * same device. `useAppStore.logout()` clears only the session keys; the
 * per-store IndexedDB is left intact.
 */
async function endSession(): Promise<void> {
  tokenStore.clear();
  await useAppStore.getState().logout();
}

export function App() {
  const [hydrating, setHydrating] = useState(true);
  const [bootError, setBootError] = useState<string | null>(null);

  // Subscribed, not read once. Quota exhaustion and multi-tab overwrites happen
  // mid-shift, long after boot — the previous module-variable read could never
  // surface them, so a till went on taking sales into a database that was no
  // longer saving them. See db/dbHealth.ts.
  const dbError = useDbHealthStore((s) => s.error);

  const { hasHydrated, activeStoreId, currentUser } = useAppStore(
    useShallow((s) => ({
      hasHydrated: s.hasHydrated,
      activeStoreId: s.activeStoreId,
      currentUser: s.currentUser,
    })),
  );

  // Warn before closing the tab while unsynced sales exist — they live only
  // on this device, and LokiJS may not have flushed the last writes yet.
  useEffect(() => installUnloadGuard(), []);

  // Two durability guarantees that have to be claimed on every till, not just
  // on the /diagnostics screen that used to be their only caller:
  //
  //   1. Persistent storage. Without it the browser may evict this origin's
  //      IndexedDB under storage pressure, and an evicted database on an
  //      offline till destroys invoices that exist nowhere else. The prompt is
  //      granted silently on an installed PWA and after engagement elsewhere;
  //      a refusal is worth recording rather than ignoring.
  //   2. A seeded unsynced count, so the unload guard above is armed for the
  //      first session too — before any sync has run to refresh it.
  useEffect(() => {
    if (!activeStoreId || !isDatabaseReady()) return;
    void requestPersistentStorage().then((granted) => {
      if (!granted) {
        console.warn(
          '[DB] Persistent storage was not granted — this browser may evict ' +
          'local data under storage pressure. Not safe for offline billing.',
        );
      }
    });
    void countUnsyncedRecords().catch(() => {
      /* counting is best-effort; the guard simply stays at its last value */
    });
  }, [activeStoreId]);

  useEffect(() => {
    void hydrateAppStore()
      .catch((err: Error) => setBootError(err.message))
      .finally(() => setHydrating(false));
  }, []);

  // Both boot screens share the login's light ground, so the hand-off from
  // "Starting…" to the login screen is not a flash from dark to light.
  if (hydrating) {
    // The same splash Desktop shows while Electron cold-starts, so the three
    // products look like one product during the wait users see most often.
    return <BrandLoader label="Starting…" />;
  }

  if (bootError || dbError) {
    return (
      <main className="login-backdrop grid min-h-screen place-items-center p-6">
        <div className="dark chrome glass-charcoal w-full max-w-md rounded-2xl border border-white/10 p-8">
          <h1 className="text-heading mb-2">Cannot start</h1>
          <p className="text-body text-fg-secondary mb-4">
            The local database could not be opened. Billing is unavailable on this
            device until it can.
          </p>
          <pre className="overflow-x-auto rounded-lg border border-danger/40 bg-danger/15 p-3 text-body text-danger-ink">
            {bootError ?? dbError}
          </pre>
        </div>
      </main>
    );
  }

  // ── The onboarding gate ────────────────────────────────────────────────────
  // A token alone is not enough to reach the till. `/auth/verify-otp` issues
  // one to any phone number that passes an OTP — including a number it has
  // never seen, for which it auto-creates a PENDING merchant. The backend
  // refuses every business endpoint for such a merchant (OnboardingGuard), so
  // the client has to route on onboarding status rather than on "has a token".
  //
  // The order below is the order the server enforces, deliberately:
  //   no session      → sign in
  //   PENDING         → registration form            (→ WAITING_APPROVAL)
  //   WAITING_APPROVAL→ poll for the approval        (→ APPROVED / REJECTED)
  //   REJECTED        → terminal
  //   APPROVED, no store → pick or create one
  //   APPROVED + store   → the app
  const hasSession = hasHydrated && tokenStore.hasSession() && Boolean(currentUser);
  if (!hasSession) return <LoginScreen />;

  const status = currentUser?.onboardingStatus;

  // An absent status means a session persisted before this field was stored.
  // The server treats absent as PENDING, but doing that here would throw a
  // merchant who has been billing for months back into the registration form.
  // So: if they already have an active store they were plainly approved, and
  // they carry on; otherwise StoreGate asks `check-status` and writes the
  // authoritative answer back, which re-renders this gate with a real status.
  if (status && status !== 'APPROVED') {
    if (status === 'PENDING') return <RegistrationScreen onLogout={endSession} />;
    if (status === 'WAITING_APPROVAL') return <WaitingApprovalScreen onLogout={endSession} />;
    // Both terminal states share a screen — see RejectedScreen. SUSPENDED is
    // listed here rather than left to fall through because it can strike an
    // account that already has an active store, and dropping such a merchant
    // into the till only to have every request 401 explains nothing.
    if (status === 'REJECTED' || status === 'SUSPENDED') {
      return <RejectedScreen onLogout={endSession} />;
    }
  }

  if (!activeStoreId) return <StoreGate onLogout={endSession} />;

  return <RouterProvider router={router} />;
}
